import { useState, useRef } from 'react';

import {
  IconButton,
  Tooltip,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Button,
  Box,
  FormControl,
  FormLabel,
  Select,
  Textarea,
  FormErrorMessage,
} from '@chakra-ui/react';

import { DeleteIcon } from '@chakra-ui/icons';

import { useMutation } from '@tanstack/react-query';

import type {
  APILeadsListResultData,
  APIStatusChangeData,
} from 'types/api.types';

import { apiLeadStatusUpdate } from 'api';

import { LEAD_STATUS_ARCHIVED } from 'views/leads/constants';

interface LeadArchiveProps {
  lead: APILeadsListResultData;
  onSuccess: (data: APIStatusChangeData) => void;
  iconSize?: 'sm' | 'md';
  isDisabled?: boolean;
}

const ARCHIVE_REASONS = {
  fake: { value: 'fake', label: 'Fake lead' },
  duplicate: { value: 'duplicate', label: 'Duplicate lead' },
  other: { value: 'other', label: 'Other' },
};

const LeadArchive = ({
  lead,
  onSuccess,
  iconSize = 'sm',
  isDisabled = false,
}: LeadArchiveProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const toast = useToast();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [isArchived, setIsArchived] = useState(false);
  const [archiveReason, setArchiveReason] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [hasError, setHasError] = useState(false);

  const { mutate, isPending } = useMutation({
    mutationFn: () => {
      const archive_reason =
        archiveReason === 'other' ? otherReason : archiveReason;
      return apiLeadStatusUpdate(lead.id, {
        status: LEAD_STATUS_ARCHIVED,
        archive_reason,
      });
    },
    onSuccess: (data) => {
      toast({
        title: 'Lead deleted',
        description: 'The lead has been successfully deleted',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });

      setIsOpen(false);
      setIsArchived(true);
      onSuccess(data);
    },
    onError: (error) => {
      toast({
        title: 'Error delete lead',
        description: 'There was an error deleting this lead. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
      setIsOpen(false);
    },
  });

  const handleArchiveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setArchiveReason('');
    setOtherReason('');
    setHasError(false);
    setIsOpen(true);
  };

  const handleArchiveConfirm = () => {
    if (!archiveReason || (archiveReason === 'other' && !otherReason)) {
      setHasError(true);
      return;
    }
    mutate();
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleReasonChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setArchiveReason(e.target.value);
    setHasError(false);
  };

  const isArchiveButtonDisabled =
    !archiveReason || (archiveReason === 'other' && !otherReason);

  if (isArchived) {
    return <>Deleted</>;
  }

  return (
    <>
      <Tooltip label="Delete lead" hasArrow placement="top">
        <IconButton
          aria-label="Delete lead"
          icon={<DeleteIcon />}
          size={iconSize}
          variant="outline"
          onClick={handleArchiveClick}
          isLoading={isPending}
          borderColor="gray.300"
          _hover={{
            backgroundColor: 'red.100',
            color: 'red.600',
            border: '1px solid',
          }}
          isDisabled={isDisabled}
        />
      </Tooltip>

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={handleClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Lead
            </AlertDialogHeader>

            <AlertDialogBody>
              <Box mb={4}>Are you sure you want to delete this lead?</Box>

              <FormControl
                isInvalid={hasError}
                isRequired
                mb={archiveReason === 'other' ? 3 : 0}
              >
                <FormLabel>Delete reason</FormLabel>
                <Select
                  variant="main"
                  placeholder="Select reason"
                  value={archiveReason}
                  onChange={handleReasonChange}
                >
                  {Object.entries(ARCHIVE_REASONS).map(
                    ([key, { value, label }]) => (
                      <option key={key} value={value}>
                        {label}
                      </option>
                    ),
                  )}
                </Select>
                {hasError && !archiveReason && (
                  <FormErrorMessage>Please select a reason</FormErrorMessage>
                )}
              </FormControl>

              {archiveReason === 'other' && (
                <FormControl isInvalid={hasError && !otherReason} isRequired>
                  <FormLabel>Specify reason</FormLabel>
                  <Textarea
                    value={otherReason}
                    onChange={(e) => setOtherReason(e.target.value)}
                    placeholder="Enter specific reason"
                    resize="vertical"
                    rows={3}
                  />
                  {hasError && !otherReason && (
                    <FormErrorMessage>Please specify a reason</FormErrorMessage>
                  )}
                </FormControl>
              )}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={handleClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleArchiveConfirm}
                ml={3}
                isLoading={isPending}
                isDisabled={isArchiveButtonDisabled}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default LeadArchive;
